import { useMemo, useState } from "react";
import styled from "styled-components";
import { useUserHasRoleOnPlatformOwner } from "@coworker/apprestructured/src/login/hooks/useUserHasRoleOnPlatformOwner";
import { useAtkomstCountries } from "../hooks/useAtkomstCountries";
import FixaSearch from "@coworker/apprestructured/src/shared/wrappers/FixaSearch/FixaSearch";
import FixaPill from "@coworker/apprestructured/src/shared/wrappers/FixaPill/FixaPill";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import FixaCheckbox from "@coworker/apprestructured/src/shared/wrappers/FixaCheckbox/FixaCheckbox";
import FixaLoadingBall from "@coworker/apprestructured/src/shared/wrappers/FixaLoading/FixaLoadingBall";
import { Avatar } from "@coworker/apprestructured/src/shared/components/Avatar/Avatar";
import { useGraphDisplayNames } from "@coworker/app/src/hooks/API/displayName.service";
import { useAtkomstNodeUsersMulti } from "../hooks/useAtkomstNodeUsers";
import { useAtkomstStoresByCountry } from "../hooks/useAtkomstStoresByCountry";

const Container = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const SectionTitle = styled.h2`
  font-size: 16px;
  font-weight: 700;
  margin: 0 0 8px;
`;

const PillRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const CountryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 8px 16px;
  border: 1px dashed #929292;
  border-radius: 4px;
  padding: 16px;
`;

const CountryHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
`;

const StoresPanel = styled.div`
  border: 1px solid #dfdfdf;
  border-radius: 4px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const CountryStoresBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CountryStoresHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const CountryLabel = styled.span`
  font-weight: 700;
  font-size: 14px;
`;

const SelectedRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
`;

const Toolbar = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;
`;

const ToolbarSearch = styled.div`
  flex: 1;
  min-width: 240px;
  max-width: 400px;
`;

const ResultCount = styled.span`
  font-size: 14px;
  color: #484848;
`;

const UserList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  border-top: 1px solid #dfdfdf;
`;

const UserRow = styled.li`
  display: grid;
  grid-template-columns: 56px 1fr auto;
  gap: 16px;
  padding: 12px 4px;
  align-items: center;
  border-bottom: 1px solid #dfdfdf;
`;

const UserName = styled.div`
  font-weight: 700;
  font-size: 14px;
`;

const UserEmail = styled.div`
  font-size: 13px;
  color: #484848;
`;

const RolesCell = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  justify-content: flex-end;
`;

type RoleStyle = { bg: string; border: string; fg: string };

const ROLE_STYLES: Record<string, RoleStyle> = {
  "Fixa Super User": {
    bg: "#E6F4EA",
    border: "#0A8A3C",
    fg: "#0A6E2F",
  },
  "Fixa Country Super User": {
    bg: "#FFF4E5",
    border: "#C2410C",
    fg: "#9A3412",
  },
  "Fixa Coworker": {
    bg: "#E8F1FB",
    border: "#0058A3",
    fg: "#003E72",
  },
};
const DEFAULT_ROLE_STYLE: RoleStyle = {
  bg: "#F5F5F5",
  border: "#929292",
  fg: "#484848",
};

const ROLE_PRECEDENCE: Record<string, number> = {
  "Fixa Super User": 0,
  "Fixa Country Super User": 1,
  "Fixa Coworker": 2,
};
const NO_ROLE_PRECEDENCE = 99;

function getPrimaryRolePrecedence(roles: string[]): number {
  if (!roles.length) return NO_ROLE_PRECEDENCE;
  return roles.reduce(
    (min, role) => Math.min(min, ROLE_PRECEDENCE[role] ?? 50),
    NO_ROLE_PRECEDENCE
  );
}

const RoleBadge = styled.span<{ $bg: string; $border: string; $fg: string }>`
  background: ${(p) => p.$bg};
  border: 1px solid ${(p) => p.$border};
  color: ${(p) => p.$fg};
  border-radius: 12px;
  padding: 2px 10px;
  font-size: 12px;
  font-weight: 500;
`;

const HeaderRow = styled.li`
  display: grid;
  grid-template-columns: 56px 1fr auto;
  gap: 16px;
  padding: 10px 4px;
  align-items: center;
  background: #f5f5f5;
  border-bottom: 1px solid #dfdfdf;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #484848;
`;

const SortButton = styled.button`
  background: none;
  border: none;
  padding: 4px 8px;
  cursor: pointer;
  font: inherit;
  color: inherit;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border-radius: 4px;

  &:hover {
    background: #ebebeb;
  }
`;

const PaginationBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 0;
`;

const PageInfo = styled.span`
  font-size: 14px;
`;

const EmptyState = styled.div`
  padding: 40px;
  text-align: center;
  color: #484848;
`;

const ErrorBanner = styled.div`
  padding: 12px 16px;
  margin: 8px 0;
  border-radius: 4px;
  background: #ffe3e3;
  border: 1px solid #d92d20;
  color: #7a271a;
`;

const PAGE_SIZE = 50;

const formatNodeLabel = (nodeRef: string, storeName?: string) => {
  const [type, id] = nodeRef.split(".");
  if (type === "RU") return `Country ${id}`;
  return storeName ? `${id} — ${storeName}` : `Store ${id}`;
};

const AtkomstUsersPage = () => {
  const { data: isPlatformOwner, isLoading: isCheckingRole } =
    useUserHasRoleOnPlatformOwner();
  const { data: countries = [], isLoading: isLoadingCountries } =
    useAtkomstCountries(!!isPlatformOwner);

  const [selectedNodeRefs, setSelectedNodeRefs] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [roleSortDir, setRoleSortDir] = useState<"none" | "asc" | "desc">(
    "none"
  );

  const selectedCountryIds = useMemo(
    () =>
      selectedNodeRefs
        .filter((ref) => ref.startsWith("RU."))
        .map((ref) => ref.slice(3)),
    [selectedNodeRefs]
  );

  const { storesByCountry: rawStoresByCountry, isLoading: isLoadingStores } =
    useAtkomstStoresByCountry(selectedCountryIds);

  const { countryNodes, nodeNameByRef, storesByCountry } = useMemo(() => {
    const sortedCountries = [...countries].sort((a, b) =>
      a.name.localeCompare(b.name)
    );
    const countryRefs = sortedCountries.map((c) => ({
      ref: `RU.${c.isoCode}`,
      name: c.name,
    }));
    const map = new Map<string, string>();
    countryRefs.forEach((c) => map.set(c.ref, c.name));
    const byCountry = new Map<
      string,
      { ref: string; name: string; countryId: string }[]
    >();
    rawStoresByCountry.forEach((stores, countryId) => {
      const storeRefs = stores
        .filter((s) => !s.storeId.includes("#"))
        .map((s) => ({
          ref: `STO.${s.storeId}`,
          name: s.name,
          countryId,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
      storeRefs.forEach((s) => map.set(s.ref, s.name));
      byCountry.set(countryId, storeRefs);
    });
    return {
      countryNodes: countryRefs,
      nodeNameByRef: map,
      storesByCountry: byCountry,
    };
  }, [countries, rawStoresByCountry]);

  const {
    data: mergedUsers,
    isLoading: isLoadingUsers,
    isFetching: isFetchingUsers,
    isError: isErrorUsers,
  } = useAtkomstNodeUsersMulti(selectedNodeRefs);

  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) return mergedUsers;
    const q = searchQuery.toLowerCase();
    return mergedUsers.filter(
      (u) =>
        u.email.toLowerCase().includes(q) ||
        u.roles.some((r) => r.toLowerCase().includes(q))
    );
  }, [mergedUsers, searchQuery]);

  const emailsForDisplayNames = useMemo(
    () => mergedUsers.map((u) => u.email),
    [mergedUsers]
  );
  const { data: displayNames } = useGraphDisplayNames(emailsForDisplayNames);

  const fullyFilteredUsers = useMemo(() => {
    if (!searchQuery.trim() || !displayNames) return filteredUsers;
    const q = searchQuery.toLowerCase();
    return filteredUsers.filter((u) => {
      const name = displayNames[u.email] ?? "";
      return (
        u.email.toLowerCase().includes(q) ||
        name.toLowerCase().includes(q) ||
        u.roles.some((r) => r.toLowerCase().includes(q))
      );
    });
  }, [filteredUsers, displayNames, searchQuery]);

  const sortedUsers = useMemo(() => {
    if (roleSortDir === "none") return fullyFilteredUsers;
    const dir = roleSortDir === "asc" ? 1 : -1;
    return [...fullyFilteredUsers].sort((a, b) => {
      const diff =
        getPrimaryRolePrecedence(a.roles) - getPrimaryRolePrecedence(b.roles);
      if (diff !== 0) return diff * dir;
      return a.email.localeCompare(b.email);
    });
  }, [fullyFilteredUsers, roleSortDir]);

  const totalPages = Math.max(1, Math.ceil(sortedUsers.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paginatedUsers = sortedUsers.slice(
    (safePage - 1) * PAGE_SIZE,
    safePage * PAGE_SIZE
  );

  const cycleRoleSort = () => {
    setPage(1);
    setRoleSortDir((dir) =>
      dir === "none" ? "asc" : dir === "asc" ? "desc" : "none"
    );
  };

  const roleSortIndicator =
    roleSortDir === "asc" ? "▲" : roleSortDir === "desc" ? "▼" : "↕";

  const toggleNode = (nodeRef: string) => {
    setPage(1);
    setSelectedNodeRefs((prev) =>
      prev.includes(nodeRef)
        ? prev.filter((r) => r !== nodeRef)
        : [...prev, nodeRef]
    );
  };

  const toggleAllStoresInCountry = (countryId: string, select: boolean) => {
    setPage(1);
    const refs = (storesByCountry.get(countryId) ?? []).map((s) => s.ref);
    setSelectedNodeRefs((prev) => {
      const without = prev.filter((r) => !refs.includes(r));
      return select ? [...without, ...refs] : without;
    });
  };

  const clearSelection = () => {
    setSelectedNodeRefs([]);
    setPage(1);
  };

  if (isCheckingRole) {
    return (
      <Container>
        <FixaLoadingBall size="medium" />
      </Container>
    );
  }

  if (!isPlatformOwner) return null;

  return (
    <Container>
      <div>
        <h1>Atkomst Users</h1>
        <p>
          View all users configured in Atkomst for one or more stores or
          countries.
        </p>
      </div>

      <div>
        <CountryHeader>
          <SectionTitle>Countries</SectionTitle>
          {countryNodes.length > 0 && (
            <FixaButton
              type="tertiary"
              size="xsmall"
              onClick={() => {
                setPage(1);
                const allRefs = countryNodes.map((c) => c.ref);
                const allSelected = allRefs.every((r) =>
                  selectedNodeRefs.includes(r)
                );
                setSelectedNodeRefs((prev) => {
                  const without = prev.filter((r) => !allRefs.includes(r));
                  return allSelected ? without : [...without, ...allRefs];
                });
              }}
            >
              {countryNodes.every((c) => selectedNodeRefs.includes(c.ref))
                ? "Deselect all"
                : "Select all"}
            </FixaButton>
          )}
        </CountryHeader>
        {isLoadingCountries ? (
          <FixaLoadingBall size="small" />
        ) : (
          <CountryGrid>
            {countryNodes.map((node) => (
              <FixaCheckbox
                key={node.ref}
                id={`country-${node.ref}`}
                value={node.ref}
                label={node.name}
                checked={selectedNodeRefs.includes(node.ref)}
                onChange={() => toggleNode(node.ref)}
              />
            ))}
            {!countryNodes.length && <span>No countries available.</span>}
          </CountryGrid>
        )}
      </div>

      {selectedCountryIds.length > 0 && (
        <div>
          <SectionTitle>
            Stores in selected countries ({selectedCountryIds.length})
          </SectionTitle>
          <StoresPanel>
            {selectedCountryIds.map((countryId) => {
              const countryStores = storesByCountry.get(countryId) ?? [];
              const selectedInCountry = countryStores.filter((s) =>
                selectedNodeRefs.includes(s.ref)
              );
              const allSelected =
                countryStores.length > 0 &&
                selectedInCountry.length === countryStores.length;
              return (
                <CountryStoresBlock key={countryId}>
                  <CountryStoresHeader>
                    <CountryLabel>
                      {countryId} — {countryStores.length} store
                      {countryStores.length === 1 ? "" : "s"}
                    </CountryLabel>
                    {countryStores.length > 0 && (
                      <FixaButton
                        type="tertiary"
                        size="xsmall"
                        onClick={() =>
                          toggleAllStoresInCountry(countryId, !allSelected)
                        }
                      >
                        {allSelected
                          ? "Deselect all stores"
                          : "Select all stores"}
                      </FixaButton>
                    )}
                  </CountryStoresHeader>
                  {countryStores.length === 0 ? (
                    <span>
                      {isLoadingStores
                        ? "Loading stores…"
                        : `No stores found for ${countryId}.`}
                    </span>
                  ) : (
                    <PillRow>
                      {countryStores.map((node) => (
                        <FixaPill
                          key={node.ref}
                          label={formatNodeLabel(node.ref, node.name)}
                          selected={selectedNodeRefs.includes(node.ref)}
                          onClick={() => toggleNode(node.ref)}
                          size="small"
                        />
                      ))}
                    </PillRow>
                  )}
                </CountryStoresBlock>
              );
            })}
          </StoresPanel>
        </div>
      )}

      {selectedNodeRefs.length > 0 && (
        <div>
          <SectionTitle>Selected ({selectedNodeRefs.length})</SectionTitle>
          <SelectedRow>
            {selectedNodeRefs.map((ref) => (
              <FixaPill
                key={ref}
                label={formatNodeLabel(ref, nodeNameByRef.get(ref))}
                selected
                onClick={() => toggleNode(ref)}
                size="small"
              />
            ))}
            <FixaButton type="tertiary" size="small" onClick={clearSelection}>
              Clear all
            </FixaButton>
          </SelectedRow>
        </div>
      )}

      {selectedNodeRefs.length === 0 ? (
        <EmptyState>
          Select one or more countries above. Stores within the selected
          countries will appear, and you can refine further by picking
          individual stores.
        </EmptyState>
      ) : (
        <>
          <Toolbar>
            <ToolbarSearch>
              <FixaSearch
                id="atkomst-users-search"
                placeholder="Search by name, email, or role"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.currentTarget.value);
                  setPage(1);
                }}
                onClear={() => {
                  setSearchQuery("");
                  setPage(1);
                }}
              />
            </ToolbarSearch>
            <ResultCount>
              {isLoadingUsers
                ? "Loading…"
                : `${fullyFilteredUsers.length} user${
                    fullyFilteredUsers.length === 1 ? "" : "s"
                  }`}
              {isFetchingUsers && !isLoadingUsers ? " (refreshing…)" : ""}
            </ResultCount>
          </Toolbar>

          {isErrorUsers && (
            <ErrorBanner>
              Failed to load users for one or more selected nodes. Please try
              again.
            </ErrorBanner>
          )}

          {isLoadingUsers ? (
            <FixaLoadingBall size="medium" />
          ) : (
            <>
              <UserList>
                <HeaderRow>
                  <span />
                  <span>User</span>
                  <SortButton
                    type="button"
                    onClick={cycleRoleSort}
                    aria-label={`Sort by role (${roleSortDir})`}
                  >
                    Role <span aria-hidden>{roleSortIndicator}</span>
                  </SortButton>
                </HeaderRow>
                {paginatedUsers.map((user) => {
                  const displayName = displayNames?.[user.email] ?? "";
                  const noRole = user.roles.length === 0;
                  return (
                    <UserRow key={user.email}>
                      <Avatar isBigImage={false} uid={user.email} />
                      <div>
                        <UserName>{displayName || user.email}</UserName>
                        {displayName && <UserEmail>{user.email}</UserEmail>}
                      </div>
                      <RolesCell>
                        {noRole ? (
                          <RoleBadge
                            $bg={DEFAULT_ROLE_STYLE.bg}
                            $border={DEFAULT_ROLE_STYLE.border}
                            $fg={DEFAULT_ROLE_STYLE.fg}
                          >
                            No role
                          </RoleBadge>
                        ) : (
                          user.roles.map((role) => {
                            const style =
                              ROLE_STYLES[role] ?? DEFAULT_ROLE_STYLE;
                            return (
                              <RoleBadge
                                key={role}
                                $bg={style.bg}
                                $border={style.border}
                                $fg={style.fg}
                              >
                                {role}
                              </RoleBadge>
                            );
                          })
                        )}
                      </RolesCell>
                    </UserRow>
                  );
                })}
              </UserList>

              {fullyFilteredUsers.length === 0 && (
                <EmptyState>No users match the current filters.</EmptyState>
              )}

              {totalPages > 1 && (
                <PaginationBar>
                  <FixaButton
                    type="secondary"
                    size="small"
                    disabled={safePage <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Previous
                  </FixaButton>
                  <PageInfo>
                    Page {safePage} of {totalPages}
                  </PageInfo>
                  <FixaButton
                    type="secondary"
                    size="small"
                    disabled={safePage >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    Next
                  </FixaButton>
                </PaginationBar>
              )}
            </>
          )}
        </>
      )}
    </Container>
  );
};

export default AtkomstUsersPage;
